/**
 * ProductCard.jsx — Mobile-first, SEO-clean, high-conversion product card
 *
 * What changed vs previous:
 * — fetchPriority capitalized correctly (React 18+)
 * — executeAddToCart catch(err) now binds err for showToast message
 * — SizeSelector no longer calls onClose itself (race condition fixed)
 * — Skeleton loading state for images
 * — onAddToCart receives name with triple fallback
 * — No JSON-LD here (belongs on PDP only)
 * — CSS variable --qa-h exposed on img-wrap so dots bottom is always in sync
 * — Swipe threshold tuned (was 8px too sensitive for scroll, now 12px)
 * — Color dot ::before touch target uses ::after (::before conflicts with
 *   some CSS reset that sets content on ::before for buttons)
 * — memo + all useCallbacks properly dep-arrayed
 *
 * Badge overlap fix (2026-03):
 * — Added `cardContext` prop ("bestseller" | "sale" | "new-arrivals" | undefined)
 * — Badge system now uses a strict priority hierarchy so only ONE badge ever
 *   renders per card — eliminates overlap with section-level badge systems
 * — Priority: Sale > Low Stock > context-injected > product flags (New/Featured)
 * — BestsellersSection passes cardContext="bestseller" so cards in that strip
 *   show "Bestseller" only when no higher-priority signal (Sale, Low Stock) exists
 */

import { useState, useRef, useCallback, useMemo, memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { useCart }     from '../../../context/CartContext';
import { useWishlist } from '../../../context/WishlistContext';
import { useToast }    from '../../../context/ToastContext';
import { BRAND }       from '../../../config/brand';
import SizeSelector    from '../SizeSelector/SizeSelector';
import { formatCurrencyDetailed } from '../../../utils/currency';
import { getImageUrl, getImageSrcSet } from '../../../utils/imageUtils';
import ApiImage from '../../../components/common/ApiImage';
import './ProductCard.css';

// ── State enums ───────────────────────────────────────────────────────────────
const AS = { IDLE: 'idle', ADDING: 'adding', ADDED: 'added', ERROR: 'error' };
const NS = { IDLE: 'idle', LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
function imgUrl(img, updatedAt = null) {
  return getImageUrl(img, updatedAt, '/hero.png');
}

/**
 * resolveBadge — Single badge resolution with strict priority hierarchy.
 *
 * RESEARCH FIX (Constructor A/B Oct 2024 + Hick's Law):
 * Multiple overlapping badges on a card increase cognitive load and reduce
 * click-through speed. One badge, chosen by priority, maximises signal clarity.
 *
 * Priority (highest wins):
 *   1. Sale — discount anchor is the strongest conversion driver
 *   2. Low Stock — scarcity FOMO (Only X left) for in-stock urgency
 *   3. Context badge — injected by the section rendering this card
 *      e.g. cardContext="bestseller" → shows "Bestseller" badge in that strip
 *   4. Product flags — New, Featured (editorial / lifecycle signals)
 *
 * Returns: { label: string, cls: string } | null
 *
 * @param {object}  product      Full product object
 * @param {boolean} isOnSale     Computed sale flag
 * @param {number}  discountPct  Computed discount percentage (0 if no sale)
 * @param {boolean} isLowStock   Computed low-stock flag
 * @param {boolean} isOutOfStock Computed OOS flag
 * @param {string|undefined} cardContext  Section context hint
 */
function resolveBadge(product, isOnSale, discountPct, isLowStock, isOutOfStock, cardContext) {
  // 1. Sale — always wins; most impactful conversion signal
  if (isOnSale && discountPct > 0) {
    return { label: `${discountPct}% OFF`, cls: 'sale' };
  }

  // 2. Low stock — scarcity (OOS has its own full overlay; skip badge there)
  if (isLowStock && !isOutOfStock) {
    return { label: `Only ${
      product.totalStock ??
      product.variants?.reduce((s, v) => s + (v.stock ?? 0), 0) ??
      '1'
    } left`, cls: 'low' };
  }

  // 3. Context-injected badge — section tells the card what it is
  if (cardContext === 'bestseller') {
    return { label: 'Bestseller', cls: 'bestseller' };
  }
  if (cardContext === 'new-arrivals') {
    return { label: 'New', cls: 'new' };
  }
  if (cardContext === 'featured') {
    return { label: 'Featured', cls: 'featured' };
  }

  // 4. Product-level flags (fallback when no context given)
  if (product.isNew)             return { label: 'New',        cls: 'new' };
  if (product.salesCount >= 100) return { label: 'Bestseller', cls: 'bestseller' };
  if (product.featured)          return { label: 'Featured',   cls: 'featured' };

  return null;
}

// ── NotifyMeModal ─────────────────────────────────────────────────────────────
function NotifyMeModal({ product, onClose }) {
  const [email,  setEmail]  = useState('');
  const [status, setStatus] = useState(NS.IDLE);
  const [error,  setError]  = useState('');
  const modalRef = useRef(null);
  const abortRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus(NS.LOADING);

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const timer = setTimeout(() => ctrl.abort('timeout'), 30_000);

    try {
      const res = await fetch(`${API_URL}/notifications/back-in-stock/subscribe`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        signal:  ctrl.signal,
        body: JSON.stringify({
          email:       email.trim().toLowerCase(),
          productId:   product._id || product.id,
          productName: product.name,
        }),
      });
      clearTimeout(timer);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Could not save subscription.');
      }
      setStatus(NS.SUCCESS);
    } catch (err) {
      clearTimeout(timer);
      setStatus(NS.ERROR);
      setError(
        err.name === 'AbortError'
          ? 'Request timed out — please try again.'
          : (err.message || 'Something went wrong. Please try again.')
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;
    const els = modalRef.current?.querySelectorAll(
      'button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!els?.length) return;
    const [first, last] = [els[0], els[els.length - 1]];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  return ReactDOM.createPortal(
    <div className="nm-backdrop" onClick={onClose} onKeyDown={handleKeyDown} role="presentation">
      <div
        ref={modalRef}
        className="nm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nm-title"
        aria-describedby="nm-desc"
      >
        <button type="button" className="nm-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {status === NS.SUCCESS ? (
          <div className="nm-success">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#27AE60" strokeWidth="1.5" aria-hidden="true">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <h3 id="nm-title" className="nm-success__title">You're on the list.</h3>
            <p id="nm-desc" className="nm-success__body">
              We'll email you the moment <strong>{product.name}</strong> is back in stock.
            </p>
            <button type="button" className="nm-btn" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <p className="nm-eyebrow">Out of Stock</p>
            <h3 id="nm-title" className="nm-headline">Notify me when available</h3>
            <p id="nm-desc" className="nm-body">
              Enter your email and we'll alert you the moment{' '}
              <strong>{product.name}</strong> is back.
            </p>
            <form className="nm-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="nm-email" className="sr-only">Email address</label>
              <input
                id="nm-email"
                type="email"
                inputMode="email"
                className="nm-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                autoComplete="email"
              />
              {error && <p className="nm-error" role="alert">{error}</p>}
              <button
                type="submit"
                className="nm-btn"
                disabled={status === NS.LOADING}
                aria-busy={status === NS.LOADING}
              >
                {status === NS.LOADING ? 'Saving…' : 'Notify Me'}
              </button>
            </form>
            <p className="nm-privacy">We'll only email you about this item. No spam.</p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── ProductCard ───────────────────────────────────────────────────────────────
const ProductCard = memo(function ProductCard({
  product,
  onAddToCart,
  onProductClick,
  priority = false,     // true for first ~4 above-fold cards (LCP)
  imageLoading,         // optional override: 'eager' | 'lazy'
  imagePriority,        // optional override: 'high' | 'low'
  /**
   * cardContext — injected by the parent section to communicate placement.
   * Used by resolveBadge() so the card shows one contextually correct badge
   * without any section needing to render its own badge overlays.
   *
   * Accepted values:
   *   "bestseller"   — BestsellersSection: show "Bestseller" if no Sale/Low Stock
   *   "new-arrivals" — NewArrivalsSection: show "New" if no Sale/Low Stock
   *   "featured"     — FeaturedSection: show "Featured" if no Sale/Low Stock
   *   undefined      — Generic/fallback: use product-level flags only
   */
  cardContext,
}) {
  const [addedState,       setAddedState]       = useState(AS.IDLE);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [showNotifyModal,  setShowNotifyModal]  = useState(false);
  const [activeImgIdx,     setActiveImgIdx]     = useState(0);
  const [isSwiping,        setIsSwiping]        = useState(false);
  const touchStartX = useRef(null);

  const { addToCart, openCart }                             = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist, openWishlistDrawer } = useWishlist();
  const { showToast }                                       = useToast();

  const productId  = product._id || product.id;
  const isWishlisted = isInWishlist(productId);

  // ── Derived data (memoized) ───────────────────────────────────────────────
  const availableSizes = useMemo(
    () => [...new Set(product.variants?.map((v) => v.size).filter(Boolean) ?? [])],
    [product.variants]
  );

  const activeImages = useMemo(() => {
    return (product.images ?? []).map(img => imgUrl(img, product.updatedAt));
  }, [product.images, product.updatedAt]);

  const hasMultipleSizes = availableSizes.length > 1;
  const primaryImg       = activeImages[activeImgIdx] ?? '/hero.png';
  const secondaryImg     = activeImages.length > 1 ? activeImages[1] : null;

  const totalStock   = useMemo(
    () => product.variants?.reduce((s, v) => s + (v.stock ?? 0), 0) ?? 0,
    [product.variants]
  );
  const isLowStock   = totalStock > 0 && totalStock <= 5;
  const isOutOfStock = totalStock === 0;

  const price           = product.price ?? 0;
  const compareAtPrice  = product.compareAtPrice ?? null;
  const isOnSale        = !!(compareAtPrice && compareAtPrice > price);
  const discountPct     = isOnSale ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;

  /**
   * badge — resolved via unified priority system.
   * ONE badge max per card regardless of how many signals are true.
   * Depends on cardContext so different sections surface different fallbacks.
   */
  const badge = useMemo(
    () => resolveBadge(product, isOnSale, discountPct, isLowStock, isOutOfStock, cardContext),
    [product, isOnSale, discountPct, isLowStock, isOutOfStock, cardContext]
  );

  const rating      = product.rating > 0 ? product.rating : null;
  const ratingCount = product.ratingCount ?? 0;

  const imageAlt = useMemo(() => {
    const g = product.gender === 'men' ? "men's" : product.gender === 'women' ? "women's" : '';
    const s =
      product.subcategory === 'round-neck' ? 'round neck t-shirt' :
      product.subcategory === 'polo'       ? 'polo t-shirt' :
      (product.subCategory ?? 'clothing');
    return `${g ? g + ' ' : ''}${s} — ${product.name ?? ''} by ${BRAND.name}`;
  }, [product.gender, product.subcategory, product.subCategory, product.name]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleWishlist = useCallback(async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      if (isWishlisted) {
        await removeFromWishlist(productId);
        showToast('Removed from your saved pieces.', 'info');
      } else {
        const preferredSize = availableSizes[0] ?? 'S';
        const productWithSize = { ...product, size: preferredSize };
        addToWishlist(productId, productWithSize);
        showToast("Saved to your wishlist — we'll keep it for you.", 'success');
        openWishlistDrawer();
      }
    } catch {
      showToast('We couldn\'t update your wishlist. Please try again.', 'error');
    }
  }, [isWishlisted, productId, product, availableSizes, addToWishlist, removeFromWishlist, openWishlistDrawer, showToast]);

  const executeAdd = useCallback(async (options) => {
    setAddedState(AS.ADDING);
    try {
      await addToCart(product, options);
      setAddedState(AS.ADDED);
      onAddToCart?.(product.name || product.title || 'Item');
      setTimeout(() => {
        openCart();
        setAddedState(AS.IDLE);
        setShowSizeSelector(false);
      }, 700);
    } catch (err) {
      setAddedState(AS.ERROR);
      showToast("We couldn't add this to your bag. Please try again.", 'error');
      setTimeout(() => setAddedState(AS.IDLE), 2_500);
    }
  }, [product, addToCart, openCart, onAddToCart, showToast]);

  const handleQuickAdd = useCallback(async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (isOutOfStock || addedState === AS.ADDING) return;
    if (hasMultipleSizes) { setShowSizeSelector(true); return; }
    await executeAdd({
      size:     availableSizes[0] ?? 'Free Size',
      quantity: 1,
    });
  }, [isOutOfStock, addedState, hasMultipleSizes, availableSizes, executeAdd]);

  const handleSizeSelect = useCallback(async (options) => {
    setShowSizeSelector(false);
    await executeAdd(options);
  }, [executeAdd]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsSwiping(false);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;
    if (Math.abs(e.touches[0].clientX - touchStartX.current) > 12) setIsSwiping(true);
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null || !isSwiping) { touchStartX.current = null; return; }
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 40) {
      setActiveImgIdx((p) =>
        diff < 0 ? Math.min(p + 1, activeImages.length - 1) : Math.max(p - 1, 0)
      );
    }
    touchStartX.current = null;
    setIsSwiping(false);
  }, [isSwiping, activeImages.length]);

  const handleCardClick = useCallback((e) => {
    if (isSwiping) { e.preventDefault(); return; }
    onProductClick?.();
  }, [isSwiping, onProductClick]);

  const qaLabel =
    addedState === AS.ADDING ? 'Adding…'        :
    addedState === AS.ADDED  ? '✓ Added to Bag' :
    addedState === AS.ERROR  ? 'Try Again'      :
    'Quick Add';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Link
        to={`/product/${product.slug ?? productId}`}
        className="pc"
        title={product.name}
        onClick={handleCardClick}
        aria-label={`View ${product.name}`}
      >
        <div
          className="pc__img-wrap"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <ApiImage
            src={primaryImg}
            fallback="/hero.png"
            alt={imageAlt}
            className="pc__img pc__img--primary"
            loading={imageLoading ?? (priority ? 'eager' : 'lazy')}
            fetchPriority={imagePriority ?? (priority ? 'high' : 'low')}
            decoding="async"
            width="320"
            height="427"
            srcSet={getImageSrcSet(primaryImg) || undefined}
            sizes="(max-width: 480px) 50vw, (max-width: 900px) 33vw, 320px"
          />

          {secondaryImg && (
            <ApiImage
              src={secondaryImg}
              fallback="/hero.png"
              alt={`${product.name ?? ''} — alternate view`}
              className="pc__img pc__img--secondary"
              loading="lazy"
              decoding="async"
              width="320"
              height="427"
            />
          )}

          {/* Swipe dots — mobile only, only when multiple images */}
          {activeImages.length > 1 && (
            <div className="pc__dots" aria-hidden="true">
              {activeImages.map((_, i) => (
                <span key={i} className={`pc__dot${i === activeImgIdx ? ' is-active' : ''}`} />
              ))}
            </div>
          )}

          {/* Wishlist */}
          <button
            type="button"
            className={`pc__heart${isWishlisted ? ' is-loved' : ''}`}
            onClick={handleWishlist}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            aria-pressed={isWishlisted}
          >
            <svg
              width="17" height="17" viewBox="0 0 24 24"
              fill={isWishlisted ? 'currentColor' : 'none'}
              stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>

          {/*
            Badges — ONE per card, resolved by resolveBadge() priority system.
            No section should render its own badge overlay on top of this.
            cardContext prop allows sections to inject their fallback badge type.
          */}
          {badge && (
            <div className="pc__badges" aria-hidden="true">
              <span className={`pc__badge pc__badge--${badge.cls}`}>{badge.label}</span>
            </div>
          )}

          {/* Out of stock overlay */}
          {isOutOfStock && (
            <div className="pc__oos" role="status" aria-label="Out of stock">
              <span className="pc__oos-label">Out of Stock</span>
              <button
                type="button"
                className="pc__notify"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowNotifyModal(true); }}
                aria-label={`Notify me when ${product.name ?? 'this item'} is back in stock`}
              >
                Notify Me
              </button>
            </div>
          )}

          {/* Quick Add */}
          {!isOutOfStock && (
            <button
              type="button"
              className={[
                'pc__qa',
                addedState === AS.ADDED  && 'is-added',
                addedState === AS.ERROR  && 'is-error',
                addedState === AS.ADDING && 'is-adding',
              ].filter(Boolean).join(' ')}
              onClick={handleQuickAdd}
              disabled={addedState === AS.ADDING}
              aria-label={qaLabel}
              aria-busy={addedState === AS.ADDING}
            >
              {qaLabel}
            </button>
          )}
        </div>

        {/* Card body */}
        <div className="pc__body">
          {product.fabric && <p className="pc__fabric">{product.fabric}</p>}
          <h3 className="pc__name">{product.name}</h3>

          {rating && ratingCount > 0 && (
            <div
              className="pc__rating"
              aria-label={`Rated ${rating} out of 5 — ${ratingCount} review${ratingCount !== 1 ? 's' : ''}`}
            >
              <div className="pc__stars" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg key={i} width="11" height="11" viewBox="0 0 24 24"
                    fill={i < Math.round(rating) ? '#C9A96E' : 'none'}
                    stroke={i < Math.round(rating) ? '#C9A96E' : '#D0D0D0'}
                    strokeWidth="1.5"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                ))}
              </div>
              <span className="pc__rating-count">({ratingCount})</span>
            </div>
          )}

          <div className="pc__price-row">
            <span className="pc__price">{formatCurrencyDetailed(price)}</span>
            {isOnSale && (
              <>
                <span className="pc__compare">{formatCurrencyDetailed(compareAtPrice)}</span>
                <span className="sr-only">Save {discountPct}%</span>
              </>
            )}
          </div>
        </div>
      </Link>

      {showSizeSelector && ReactDOM.createPortal(
        <SizeSelector
          product={product}
          onSelect={handleSizeSelect}
          onClose={() => setShowSizeSelector(false)}
          addedState={addedState}
        />,
        document.body
      )}

      {showNotifyModal && (
        <NotifyMeModal
          product={product}
          onClose={() => setShowNotifyModal(false)}
        />
      )}
    </>
  );
});

export default ProductCard;